import React, { useEffect, useMemo, useRef, useState } from 'react';
import { netlabApi } from './api';

const CATEGORY_LABEL = { consumption: 'consumption', generation: 'generation', storage: 'charge / discharge' };

export function fmtEnergy(kwh) {
  if (kwh == null) return '';
  if (Math.abs(kwh) >= 1e6) return `${(kwh / 1e6).toFixed(2)} GWh`;
  if (Math.abs(kwh) >= 1e3) return `${(kwh / 1e3).toFixed(2)} MWh`;
  return `${kwh.toFixed(0)} kWh`;
}

export function fmtKw(kw) {
  if (kw == null) return '';
  return Math.abs(kw) >= 1000 ? `${(kw / 1000).toFixed(2)} MW` : `${kw.toFixed(kw < 10 ? 2 : 1)} kW`;
}

// Small area chart of a profile summary ({points, peak_kw, ...}).
export function Sparkline({ summary, color = '#c084fc', height = 54 }) {
  const pts = summary?.points || [];
  const path = useMemo(() => {
    if (pts.length < 2) return null;
    const max = Math.max(...pts.map(Math.abs), 1e-9);
    const min = Math.min(0, ...pts);
    const span = max - min || 1;
    const xy = pts.map((v, i) => [(i / (pts.length - 1)) * 100, 100 - ((v - min) / span) * 100]);
    const line = xy.map(([x, y], i) => `${i ? 'L' : 'M'}${x.toFixed(2)},${y.toFixed(2)}`).join(' ');
    const zero = 100 - ((0 - min) / span) * 100;
    return { line, area: `${line} L100,${zero.toFixed(2)} L0,${zero.toFixed(2)} Z` };
  }, [pts]);
  if (!path) return null;
  return (
    <svg className="nl-spark" viewBox="0 0 100 100" preserveAspectRatio="none" style={{ height }}
      role="img" aria-label={`Profile, peak ${fmtKw(summary.peak_kw)}`}>
      <path d={path.area} fill={color} fillOpacity="0.22" />
      <path d={path.line} fill="none" stroke={color} strokeWidth="1.4" vectorEffect="non-scaling-stroke" />
    </svg>
  );
}

export function ProfileStats({ summary, timeline }) {
  if (!summary) return null;
  const tl = timeline || summary.timeline;
  // On coarse timelines each value is an average, so the max is not the rated peak.
  const peakLabel = tl && tl.step_hours > 1 ? `max ${tl.step} avg` : 'peak';
  return (
    <div className="nl-profile-stats">
      <span>{peakLabel} <b>{fmtKw(summary.peak_kw)}</b></span>
      <span>mean <b>{fmtKw(summary.mean_kw)}</b></span>
      <span><b>{fmtEnergy(summary.energy_kwh)}</b> / period</span>
      {tl && <span className="nl-muted-inline">{tl.length} × {tl.step}</span>}
    </div>
  );
}

// Profile picker inside the "Add asset" card. `value` is the profile spec sent to the backend.
export default function ProfileSection({ sid, kind, pMw, value, onChange }) {
  const [options, setOptions] = useState(null);
  const [error, setError] = useState('');
  const [pool, setPool] = useState({ columns: [], total: 0 });
  const [poolQuery, setPoolQuery] = useState('');
  const [upload, setUpload] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [preview, setPreview] = useState(null);
  const [previewError, setPreviewError] = useState('');
  const fileRef = useRef(null);
  const v = value || { source: 'none' };
  const set = (patch) => onChange({ ...v, ...patch });

  useEffect(() => {
    let cancelled = false;
    setOptions(null);
    setError('');
    netlabApi.profileOptions(sid, kind)
      .then(d => { if (!cancelled) setOptions(d); })
      .catch(e => !cancelled && setError(e.message));
    return () => { cancelled = true; };
  }, [sid, kind]);

  // Area pool list (debounced search).
  useEffect(() => {
    if (v.source !== 'pool' || !options?.category) return undefined;
    const t = setTimeout(() => {
      netlabApi.profilePool(sid, options.category, poolQuery)
        .then(d => {
          setPool(d);
          if (!v.column && d.columns.length) onChange({ ...v, column: d.columns[0].column });
        })
        .catch(e => setError(e.message));
    }, 250);
    return () => clearTimeout(t);
    // eslint-disable-next-line
  }, [v.source, poolQuery, options?.category, sid]);

  // Live preview of the resulting kW series.
  const previewKey = JSON.stringify([kind, v, pMw]);
  useEffect(() => {
    setPreview(null);
    setPreviewError('');
    if (v.source === 'none') return undefined;
    if (v.source === 'pool' && !v.column) return undefined;
    if (v.source === 'copy' && !v.element) return undefined;
    if (v.source === 'upload' && !v.upload_id) return undefined;
    const t = setTimeout(() => {
      netlabApi.profilePreview(sid, kind, v, pMw)
        .then(d => setPreview(d.preview))
        .catch(e => setPreviewError(e.message));
    }, 300);
    return () => clearTimeout(t);
    // eslint-disable-next-line
  }, [previewKey, sid]);

  if (error) return <div className="nl-error">{error}</div>;
  if (!options) return <div className="nl-muted">Loading profile options…</div>;
  if (!options.category) return null;

  const src = options.sources;
  const sources = [
    { value: 'none', label: kind === 'battery' ? 'Automatic: store surplus PV (no profile)' : `No profile (static p_mw)` },
    src.synthetic && { value: 'synthetic', label: 'Standard shape (generated)' },
    src.pool > 0 && { value: 'pool', label: `From this area (${src.pool} NetBank profiles)` },
    src.copy.length > 0 && { value: 'copy', label: `Copy from an existing asset (${src.copy.length})` },
    src.upload && { value: 'upload', label: 'Upload a file (.csv / .parquet)' },
  ].filter(Boolean);

  const onFile = async (file) => {
    if (!file) return;
    setUploading(true);
    setPreviewError('');
    try {
      const d = await netlabApi.profileUpload(sid, file);
      setUpload(d);
      set({ upload_id: d.upload_id, column: d.columns[0], unit: v.unit || 'kW' });
    } catch (e) {
      setPreviewError(e.message);
    } finally {
      setUploading(false);
    }
  };

  const tl = options.timeline;

  return (
    <div className="nl-profile">
      <div className="nl-subtitle">Profile · {CATEGORY_LABEL[options.category]}</div>
      <label className="nl-field">
        <span>Source</span>
        <select value={v.source} onChange={(e) => onChange({ source: e.target.value, scale: 'peak' })}>
          {sources.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
        </select>
      </label>
      {kind === 'battery' && v.source === 'none' && (
        <p className="nl-hint">
          In power flow scenarios the battery charges from surplus PV at its bus (or the whole grid's surplus if
          nothing else is on its bus) and discharges into demand, within max_p_mw and max_e_mwh, 90% round trip.
          {tl && tl.step_hours >= 24 && ' On a daily timeline it only sees daily averages, so it cannot shift PV from noon to evening.'}
        </p>
      )}

      {v.source !== 'none' && !tl && v.source !== 'upload' && (
        <label className="nl-field">
          <span>Time resolution (this net has no timeline yet — one year, 2024)</span>
          <select value={v.resolution || 'hourly'} onChange={(e) => set({ resolution: e.target.value })}>
            <option value="15min">15 minutes</option>
            <option value="hourly">Hourly</option>
            <option value="daily">Daily</option>
          </select>
        </label>
      )}

      {v.source === 'pool' && (
        <>
          <input className="nl-search" placeholder="Search profiles (e.g. P_12)…" value={poolQuery}
            onChange={(e) => setPoolQuery(e.target.value)} />
          <select className="nl-pool-list" size={5} value={v.column || ''} onChange={(e) => set({ column: e.target.value })}>
            {pool.columns.map(c => (
              <option key={c.column} value={c.column}>
                {c.column}{c.peak_kw != null ? ` · peak ${fmtKw(c.peak_kw)}` : ''}
              </option>
            ))}
          </select>
          <div className="nl-row-between">
            <small className="nl-muted">{pool.total > pool.columns.length ? `showing ${pool.columns.length} of ${pool.total}` : `${pool.total} profiles`}</small>
            <button type="button" className="nl-btn small ghost" disabled={!pool.columns.length}
              onClick={() => set({ column: pool.columns[Math.floor(Math.random() * pool.columns.length)].column })}>
              Pick random
            </button>
          </div>
        </>
      )}

      {v.source === 'copy' && (
        <label className="nl-field">
          <span>Asset to copy from</span>
          <select value={v.element || ''} onChange={(e) => set({ element: e.target.value })}>
            <option value="" disabled>Choose…</option>
            {src.copy.map(n => <option key={n} value={n}>{n}</option>)}
          </select>
        </label>
      )}

      {v.source === 'upload' && (
        <>
          <div className="nl-pickrow">
            <div className="nl-picked">
              {upload ? <><b>{upload.filename}</b><small>{upload.length} rows{upload.has_time ? ' · with time column' : ' · no time column'}</small></>
                : <small>CSV or parquet: a time column plus a value column</small>}
            </div>
            <button type="button" className="nl-btn small" disabled={uploading} onClick={() => fileRef.current?.click()}>
              {uploading ? 'Reading…' : upload ? 'Replace' : 'Choose file'}
            </button>
            <input ref={fileRef} type="file" accept=".csv,.parquet,.txt" hidden
              onChange={(e) => { onFile(e.target.files?.[0]); e.target.value = ''; }} />
          </div>
          {upload && (
            <div className="nl-grid2">
              <label className="nl-field">
                <span>Column</span>
                <select value={v.column || ''} onChange={(e) => set({ column: e.target.value })}>
                  {upload.columns.map(c => <option key={c} value={c}>{c}</option>)}
                </select>
              </label>
              <label className="nl-field">
                <span>Values are in</span>
                <select value={v.unit || 'kW'} onChange={(e) => set({ unit: e.target.value })}>
                  <option value="kW">kW</option>
                  <option value="MW">MW</option>
                </select>
              </label>
            </div>
          )}
        </>
      )}

      {v.source !== 'none' && v.source !== 'synthetic' && (
        <div className="nl-radio">
          <label>
            <input type="radio" checked={(v.scale || 'peak') === 'peak'} onChange={() => set({ scale: 'peak' })} />
            Scale so the peak equals p_mw
          </label>
          <label>
            <input type="radio" checked={v.scale === 'absolute'} onChange={() => set({ scale: 'absolute' })} />
            Use the values as they are (kW)
          </label>
        </div>
      )}
      {v.source === 'synthetic' && <p className="nl-hint">Scaled so its peak equals p_mw.</p>}

      {v.source !== 'none' && (
        <div className="nl-profile-preview">
          {previewError && <div className="nl-error">{previewError}</div>}
          {!previewError && !preview && <div className="nl-muted">Preparing preview…</div>}
          {preview && (
            <>
              <Sparkline summary={preview} color={options.category === 'generation' ? '#facc15' : '#f0abfc'} />
              <ProfileStats summary={preview} />
            </>
          )}
        </div>
      )}
    </div>
  );
}
